/**
 * 8비트 SFX — 오디오 파일 없이 OscillatorNode 로 합성한다 (에셋 예산 0).
 *
 * 체인: osc → envGain → busGain(1.0) → Compressor(-18/12/6/0.003/0.12) → masterGain(0.8) → destination
 *
 * 규칙 (UPLIFT §6.8 감사 반영):
 *  - 언락은 사용자 제스처 핸들러 **안에서 동기로** 한다. await 뒤로 밀면 iOS 가 제스처를 잃는다.
 *  - 뮤트는 게이트가 아니라 masterGain 15ms 램프 + 드론 강제 OFF. 게이트만 두면 예약된 팡파르가 끝까지 울린다.
 *  - 스케줄은 단조: at = max(currentTime + 0.012 + atMs/1000, lastAt). 캐치업 프레임에서 콤보 음이 역순이 되는 것을 막는다.
 *  - BGM 은 만들지 않는다 — 고정 박자는 속도 커브와 싸우고 모르는 사람의 선회 타이밍을 보조한다(설계원칙 2 희석).
 *    대신 페널티 중에만 울리는 저역 드론(리듬 없음, 틀린 사람만 듣는다).
 *  - 결정론과 무관한 뷰 계층이다. 엔진을 import 하지 않는다.
 */

export type CueId =
  | 'eat_correct'
  | 'eat_wrong'
  | 'eat_wrong_floor'
  | 'word_solved'
  | 'word_perfect'
  | 'word_failed'
  | 'death'
  | 'run_clear'
  | 'countdown_beep'
  | 'countdown_go'
  | 'penalty_on'
  | 'penalty_off'
  | 'relocate'
  | 'revive_ok'
  | 'revive_fail'
  | 'ui_tap'
  | 'ui_alt';

export interface Voice {
  readonly wave: OscillatorType;
  readonly hz: number | readonly [number, number]; // 고정 또는 스윕(from → to)
  readonly a: number; // attack ms
  readonly d: number; // decay ms
  readonly s: number; // sustain ms
  readonly r: number; // release ms
  readonly sus: number; // sustain 레벨 0..1 (peak 대비)
  readonly peak: number; // 게인
  readonly delay?: number; // 큐 시작 기준 오프셋 ms
}

export interface Patch {
  readonly lenMs: number; // 청감 길이 — 테스트가 WORD_CLEAR 창과 대조한다
  readonly voices: readonly Voice[];
}

export const midiToHz = (m: number): number => 440 * 2 ** ((m - 69) / 12);

/** 콤보 사다리 — 12단에서 포화(콤보 배수 상한 combo 12 와 같은 지점). */
export const COMBO_LADDER_MIDI: readonly number[] = [
  72, 74, 76, 79, 81, 84, 86, 88, 91, 93, 95, 96,
];

const seq = (
  wave: OscillatorType,
  midis: readonly number[],
  stepMs: number,
  r: number,
  peak: number,
): Voice[] =>
  midis.map((m, i) => ({
    wave,
    hz: midiToHz(m),
    a: 2,
    d: 20,
    s: Math.max(0, stepMs - 30),
    r,
    sus: 0.5,
    peak,
    delay: i * stepMs,
  }));

export const CATALOG: Readonly<Record<CueId, Patch>> = {
  // 기음 1벌 + hzScale 로 12단. sus 0.28 — 0 이면 가청 길이가 28ms(≈15주기)라 '음'이 아니라 '클릭'이다
  eat_correct: {
    lenMs: 60,
    voices: [
      { wave: 'square', hz: midiToHz(72), a: 2, d: 18, s: 0, r: 40, sus: 0.28, peak: 0.22 },
      { wave: 'square', hz: midiToHz(84), a: 2, d: 18, s: 0, r: 40, sus: 0.28, peak: 0.077 },
    ],
  },
  eat_wrong: {
    lenMs: 185,
    voices: [
      { wave: 'sawtooth', hz: [329.63, 110], a: 4, d: 0, s: 120, r: 60, sus: 1, peak: 0.16 },
      { wave: 'square', hz: [220, 82.41], a: 4, d: 0, s: 120, r: 60, sus: 1, peak: 0.1 },
    ],
  },
  eat_wrong_floor: {
    lenMs: 265,
    voices: [
      { wave: 'sawtooth', hz: [246.94, 82.41], a: 4, d: 0, s: 190, r: 70, sus: 1, peak: 0.16 },
      { wave: 'square', hz: [164.81, 61.74], a: 4, d: 0, s: 190, r: 70, sus: 1, peak: 0.1 },
    ],
  },
  // WORD_CLEAR 최단 창 = 6 × PENALTY_TICK_FLOOR_MS(60) = 360ms 안에 끝나야 한다
  word_solved: { lenMs: 320, voices: seq('square', [84, 88, 91, 96], 50, 60, 0.18) },
  word_perfect: { lenMs: 350, voices: seq('square', [84, 88, 91, 96, 108], 50, 110, 0.18) },
  word_failed: {
    lenMs: 250,
    voices: [
      { wave: 'triangle', hz: midiToHz(79), a: 3, d: 30, s: 60, r: 40, sus: 0.6, peak: 0.16 },
      {
        wave: 'triangle',
        hz: midiToHz(72),
        a: 3,
        d: 30,
        s: 90,
        r: 60,
        sus: 0.6,
        peak: 0.16,
        delay: 110,
      },
    ],
  },
  death: { lenMs: 860, voices: seq('square', [76, 74, 72, 71, 69, 67, 65, 64], 100, 80, 0.15) },
  run_clear: {
    lenMs: 910,
    voices: [
      ...seq('square', [72, 76, 79, 84, 88, 91], 90, 60, 0.17),
      {
        wave: 'square',
        hz: midiToHz(96),
        a: 3,
        d: 40,
        s: 240,
        r: 120,
        sus: 0.7,
        peak: 0.16,
        delay: 540,
      },
      {
        wave: 'square',
        hz: midiToHz(88),
        a: 3,
        d: 40,
        s: 240,
        r: 120,
        sus: 0.7,
        peak: 0.1,
        delay: 540,
      },
      {
        wave: 'square',
        hz: midiToHz(84),
        a: 3,
        d: 40,
        s: 240,
        r: 120,
        sus: 0.7,
        peak: 0.08,
        delay: 540,
      },
    ],
  },
  // F#5 / F#6 — 콤보 음계(C장조 계열) 밖이라 정답음과 섞이지 않는다
  countdown_beep: {
    lenMs: 82,
    voices: [{ wave: 'square', hz: 739.99, a: 2, d: 20, s: 20, r: 40, sus: 0.5, peak: 0.14 }],
  },
  countdown_go: {
    lenMs: 205,
    voices: [
      { wave: 'square', hz: 1479.98, a: 2, d: 30, s: 90, r: 80, sus: 0.6, peak: 0.16 },
      { wave: 'square', hz: 1108.73, a: 2, d: 30, s: 90, r: 80, sus: 0.6, peak: 0.08 },
    ],
  },
  penalty_on: {
    lenMs: 120,
    voices: [
      { wave: 'square', hz: midiToHz(50), a: 2, d: 10, s: 25, r: 20, sus: 0.6, peak: 0.14 },
      {
        wave: 'square',
        hz: midiToHz(50),
        a: 2,
        d: 10,
        s: 25,
        r: 20,
        sus: 0.6,
        peak: 0.14,
        delay: 60,
      },
    ],
  },
  penalty_off: {
    lenMs: 105,
    voices: [{ wave: 'square', hz: midiToHz(62), a: 2, d: 20, s: 40, r: 40, sus: 0.5, peak: 0.12 }],
  },
  relocate: {
    lenMs: 155,
    voices: [
      { wave: 'triangle', hz: [523.25, 783.99], a: 4, d: 0, s: 100, r: 50, sus: 1, peak: 0.14 },
    ],
  },
  revive_ok: { lenMs: 820, voices: seq('square', [60, 64, 67, 72, 76, 72], 120, 100, 0.16) },
  revive_fail: {
    lenMs: 505,
    voices: [
      { wave: 'sawtooth', hz: midiToHz(43), a: 4, d: 40, s: 160, r: 80, sus: 0.7, peak: 0.14 },
      {
        wave: 'sawtooth',
        hz: midiToHz(40),
        a: 4,
        d: 40,
        s: 200,
        r: 120,
        sus: 0.7,
        peak: 0.14,
        delay: 220,
      },
    ],
  },
  ui_tap: {
    lenMs: 36,
    voices: [{ wave: 'square', hz: midiToHz(84), a: 1, d: 10, s: 8, r: 16, sus: 0.4, peak: 0.1 }],
  },
  ui_alt: {
    lenMs: 36,
    voices: [{ wave: 'square', hz: midiToHz(79), a: 1, d: 10, s: 8, r: 16, sus: 0.4, peak: 0.1 }],
  },
};

/**
 * 무음 성립 불변식 — 소리가 꺼진 환경에서 정보 손실이 0이어야 한다.
 * 각 큐가 어떤 시각 채널로 중복 부호화되는지의 단일 출처. 테스트가 화이트리스트와 대조한다.
 */
export const CUE_VISUAL_CHANNEL: Readonly<Record<CueId, readonly string[]>> = {
  eat_correct: ['fx:eatGood', 'hud', 'band:slot'],
  eat_wrong: ['fx:eatBad', 'shake', 'hud', 'toast'],
  eat_wrong_floor: ['fx:eatBad', 'shake', 'hud', 'toast'],
  word_solved: ['band', 'hud', 'fx:bang'],
  word_perfect: ['band', 'hud', 'fx:bang', 'fx:burst'],
  word_failed: ['band', 'hud', 'fx:aigo'],
  death: ['overlay:gameover', 'fx:deathBlink'],
  run_clear: ['overlay:gameover', 'fx:burst'],
  countdown_beep: ['band:ready'],
  countdown_go: ['band:ready'],
  penalty_on: ['field:warn', 'fx:vignette', 'speed'],
  penalty_off: ['field:warn', 'speed'],
  relocate: ['fx:relocate', 'toast'],
  revive_ok: ['overlay:revive'],
  revive_fail: ['overlay:revive'],
  ui_tap: ['button'],
  ui_alt: ['button'],
};

export const VISUAL_CHANNELS: ReadonlySet<string> = new Set([
  'fx:eatGood',
  'fx:eatBad',
  'fx:bang',
  'fx:aigo',
  'fx:burst',
  'fx:relocate',
  'fx:vignette',
  'fx:deathBlink',
  'hud',
  'band',
  'band:slot',
  'band:ready',
  'shake',
  'toast',
  'speed',
  'field:warn',
  'overlay:gameover',
  'overlay:revive',
  'button',
]);

const MUTE_KEY = 'ajae.muted.v1';

export function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeMuted(v: boolean): void {
  try {
    localStorage.setItem(MUTE_KEY, v ? '1' : '0');
  } catch {
    /* 저장 실패는 무시 */
  }
}

/** 테스트에서 가짜 컨텍스트를 주입할 수 있게 최소 표면만 쓴다. */
export interface AudioLike {
  readonly currentTime: number;
  readonly destination: AudioNode;
  readonly state: string;
  resume(): Promise<void>;
  createOscillator(): OscillatorNode;
  createGain(): GainNode;
  createDynamicsCompressor(): DynamicsCompressorNode;
}

export class AudioBus {
  private ctx: AudioLike | null = null;
  private master: GainNode | null = null;
  private bus: GainNode | null = null;
  private lastAt = 0;
  private muted: boolean;
  private drone: { osc: OscillatorNode[]; gain: GainNode } | null = null;

  constructor(
    private readonly factory: () => AudioLike,
    muted = readMuted(),
  ) {
    this.muted = muted;
  }

  isMuted(): boolean {
    return this.muted;
  }

  isUnlocked(): boolean {
    return this.ctx !== null;
  }

  /** 반드시 사용자 제스처 핸들러 안에서 동기로 부른다. 뮤트 저장 사용자는 컨텍스트를 만들지 않는다. */
  unlock(): void {
    if (this.ctx !== null || this.muted) return;
    let ctx: AudioLike;
    try {
      ctx = this.factory();
    } catch {
      return; // 일부 안드로이드 WebView 에서 생성이 throw 한다 — 소리 없이 진행
    }
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -18;
    comp.knee.value = 12;
    comp.ratio.value = 6;
    comp.attack.value = 0.003;
    comp.release.value = 0.12;
    const master = ctx.createGain();
    master.gain.value = 0.8;
    const bus = ctx.createGain();
    bus.gain.value = 1.0;
    bus.connect(comp);
    comp.connect(master);
    master.connect(ctx.destination);
    this.ctx = ctx;
    this.master = master;
    this.bus = bus;
    if (ctx.state === 'suspended') void ctx.resume();
  }

  setMuted(v: boolean): void {
    this.muted = v;
    writeMuted(v);
    if (this.ctx === null || this.master === null) return;
    const now = this.ctx.currentTime;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setValueAtTime(this.master.gain.value, now);
    this.master.gain.linearRampToValueAtTime(v ? 0 : 0.8, now + 0.015); // 15ms 램프
    if (v) this.droneOff();
  }

  /**
   * 큐 재생. combo 는 eat_correct 사다리 단(1부터). atMs 는 같은 프레임 안 틱 슬롯 오프셋.
   * 예약 시각은 단조 증가한다 — 캐치업 프레임에서 콤보 음이 역순이 되지 않게.
   */
  play(cue: CueId, opts: { combo?: number; atMs?: number } = {}): number {
    if (this.muted || this.ctx === null || this.bus === null) return -1;
    const ctx = this.ctx;
    const base = Math.max(ctx.currentTime + 0.012 + (opts.atMs ?? 0) / 1000, this.lastAt);
    this.lastAt = base;
    const patch = CATALOG[cue];
    let scale = 1;
    if (cue === 'eat_correct' && opts.combo !== undefined) {
      const step = Math.min(COMBO_LADDER_MIDI.length, Math.max(1, opts.combo));
      scale = midiToHz(COMBO_LADDER_MIDI[step - 1] as number) / midiToHz(72);
    }
    for (const v of patch.voices) this.voice(ctx, this.bus, v, base + (v.delay ?? 0) / 1000, scale);
    return base;
  }

  private voice(ctx: AudioLike, out: GainNode, v: Voice, t0: number, hzScale: number): void {
    const osc = ctx.createOscillator();
    const env = ctx.createGain();
    osc.type = v.wave;
    if (typeof v.hz === 'number') {
      osc.frequency.setValueAtTime(v.hz * hzScale, t0);
    } else {
      osc.frequency.setValueAtTime(v.hz[0] * hzScale, t0);
      osc.frequency.exponentialRampToValueAtTime(
        Math.max(1, v.hz[1] * hzScale),
        t0 + (v.a + v.d + v.s) / 1000,
      );
    }
    const a = t0 + v.a / 1000;
    const d = a + v.d / 1000;
    const s = d + v.s / 1000;
    const r = s + v.r / 1000;
    env.gain.setValueAtTime(0.0001, t0);
    env.gain.linearRampToValueAtTime(v.peak, a);
    env.gain.linearRampToValueAtTime(Math.max(0.0001, v.peak * v.sus), d);
    env.gain.setValueAtTime(Math.max(0.0001, v.peak * v.sus), s);
    env.gain.exponentialRampToValueAtTime(0.0001, r);
    osc.connect(env);
    env.connect(out);
    osc.start(t0);
    osc.stop(r + 0.01);
  }

  /** 페널티 드론 — D2+A2, 리듬 없음. 에피소드(ticksLeft > 0) 기준으로 켜고 끈다. */
  droneOn(): void {
    if (this.muted || this.ctx === null || this.bus === null || this.drone !== null) return;
    const ctx = this.ctx;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.075, ctx.currentTime + 0.08);
    gain.connect(this.bus);
    const oscs = [midiToHz(38), midiToHz(45)].map((hz) => {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = hz;
      o.connect(gain);
      o.start();
      return o;
    });
    this.drone = { osc: oscs, gain };
  }

  droneOff(): void {
    if (this.drone === null || this.ctx === null) return;
    const { osc, gain } = this.drone;
    const now = this.ctx.currentTime;
    gain.gain.cancelScheduledValues(now);
    gain.gain.setValueAtTime(gain.gain.value, now);
    gain.gain.linearRampToValueAtTime(0.0001, now + 0.06);
    for (const o of osc) o.stop(now + 0.08);
    this.drone = null;
  }

  isDroning(): boolean {
    return this.drone !== null;
  }
}

/** 브라우저용 팩토리. sampleRate 를 지정하지 않는다 — 일부 안드로이드 WebView 에서 throw 한다. */
export function createBrowserAudio(): AudioLike {
  const Ctor = (
    window as unknown as { AudioContext?: new (o?: AudioContextOptions) => AudioContext }
  ).AudioContext;
  if (Ctor === undefined) throw new Error('AudioContext 없음');
  return new Ctor({ latencyHint: 'interactive' }) as unknown as AudioLike;
}
